import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import profilePictureService from '../services/profilePictureService';

interface ProfilePictureState {
  profilePictureUrl: string | null;
  profilePictureVersion: number; // Used to bust cache when image is updated
  isLoading: boolean;
  lastFetched: number | null;

  // Actions
  fetchProfilePicture: (force?: boolean) => Promise<void>;
  setProfilePicture: (url: string | null) => void;
  clearProfilePicture: () => void;
  uploadProfilePicture: (file: File) => Promise<void>;
  deleteProfilePicture: () => Promise<void>;
}

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export const useProfilePictureStore = create<ProfilePictureState>()(
  persist(
    (set, get) => ({
      profilePictureUrl: null,
      profilePictureVersion: Date.now(),
      isLoading: false,
      lastFetched: null,

      fetchProfilePicture: async (force = false) => {
        const { lastFetched, profilePictureUrl } = get();
        const now = Date.now();

        // Skip fetch if we have cached data and it's fresh (unless forced)
        if (!force && profilePictureUrl && lastFetched && (now - lastFetched) < CACHE_DURATION) {
          console.log('Using cached profile picture');
          return;
        }

        try {
          set({ isLoading: true });
          const response = await profilePictureService.getProfilePicture();

          if (response.success && response.data?.profilePictureUrl) {
            set({
              profilePictureUrl: response.data.profilePictureUrl,
              lastFetched: now,
              isLoading: false,
            });
          } else {
            set({
              profilePictureUrl: null,
              lastFetched: now,
              isLoading: false,
            });
          }
        } catch (error) {
          console.error('Error fetching profile picture:', error);
          set({ isLoading: false });
        }
      },

      setProfilePicture: (url) => {
        set({
          profilePictureUrl: url,
          profilePictureVersion: Date.now(),
          lastFetched: Date.now(),
        });
      },

      clearProfilePicture: () => {
        set({
          profilePictureUrl: null,
          lastFetched: null,
        });
      },

      uploadProfilePicture: async (file: File) => {
        try {
          set({ isLoading: true });
          const response = await profilePictureService.uploadProfilePicture(file);

          if (response.success && response.data?.profilePictureUrl) {
            set({
              profilePictureUrl: response.data.profilePictureUrl,
              profilePictureVersion: Date.now(),
              lastFetched: Date.now(),
              isLoading: false,
            });
          } else {
            throw new Error(response.message || 'Upload failed');
          }
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      deleteProfilePicture: async () => {
        try {
          set({ isLoading: true });
          await profilePictureService.deleteProfilePicture();
          set({
            profilePictureUrl: null,
            profilePictureVersion: Date.now(),
            lastFetched: Date.now(),
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },
    }),
    {
      name: 'profile-picture-storage',
      partialize: (state) => ({
        profilePictureUrl: state.profilePictureUrl,
        profilePictureVersion: state.profilePictureVersion,
        lastFetched: state.lastFetched,
      }),
    }
  )
);